import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import _ from 'lodash';
import maxSizeModifier from 'popper-max-size-modifier';
import type { ReactNode } from 'react';
import React, { useMemo, useState } from 'react';
import styles from './ContentPreviewPopOver.module.scss';
import PopOver from './deprecated/PopOver';
import PopOverContainer, {
  PopOverContainerArrow,
} from './deprecated/PopOverContainer';

const PAPER_RATIO = 8.5 / 11;
const MAX_WIDTH = 510;
const MAX_HEIGHT = MAX_WIDTH / PAPER_RATIO;

export const SCREEN_EDGE_PADDING = 12;

interface ContentPreviewPopOverProps {
  hidden?: boolean;
  display?: 'block' | 'inline-block' | 'inline';
  alignToReference?: boolean;
  allowFlip?: boolean;
  offset?: [number, number];
  minWidth?: number;
  minHeight?: number;
  children: ReactNode;
  popOver: ReactNode;
  open?: boolean;
  onOpenChange?: (newOpen: boolean) => void;
  placement?: Placement;
  longHoverDelay?: boolean;
}

const ContentPreviewPopOver = ({
  display,
  hidden,
  alignToReference,
  allowFlip,
  offset,
  minWidth = 325,
  minHeight = 250,
  children,
  popOver,
  placement: popOverPlacement = 'right',
  ...popOverProps
}: ContentPreviewPopOverProps) => {
  const [previewStyle, setPreviewStyle] = useState<{
    width: number;
    maxHeight: number;
  } | null>(null);

  const modifiers = useMemo(
    () =>
      _.compact([
        {
          name: 'offset',
          options: {
            offset: ({
              placement,
              reference,
              popper,
            }: {
              placement: string;
              reference: { height: number };
              popper: { height: number };
            }) => {
              if (offset) {
                return offset;
              }
              if (
                alignToReference &&
                (placement === 'right' || placement === 'left')
              ) {
                return [popper.height / 2 - reference.height / 2, 6];
              }
              return [0, 6];
            },
          },
        },
        { ...maxSizeModifier, options: { padding: SCREEN_EDGE_PADDING } },
        {
          name: 'applySize',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['maxSize'],
          fn: ({
            state,
          }: {
            state: {
              modifiersData: { maxSize: { width: number; height: number } };
            };
          }) => {
            const { maxSize } = state.modifiersData;
            const width = Math.min(MAX_WIDTH, maxSize.width);
            const nextPreviewStyle = {
              maxHeight: Math.floor(
                Math.min(MAX_HEIGHT, width / PAPER_RATIO, maxSize.height),
              ),
              width: Math.floor(width),
            };
            // Set React state instead of updating `state.styles` directly because this
            // modifier runs after the main phase, so `preventOverflow` and `flip` aren't
            // aware of the changes to `state.styles`, causing the position to be incorrect
            // sometimes.
            // For example, without apollo cache, hover over the mention pill,
            // the pop-over position will be incorrect as the position is for the pop over
            // without the max height/width applied.
            setPreviewStyle((current) =>
              _.isEqual(current, nextPreviewStyle) ? current : nextPreviewStyle,
            );
          },
        },
        {
          name: 'preventOverflow',
          options: { padding: SCREEN_EDGE_PADDING },
        },
        allowFlip
          ? {
              name: 'flip',
              options: { fallbackPlacements: ['left', 'bottom', 'top'] },
            }
          : undefined,
      ]),
    [alignToReference, offset, allowFlip],
  );

  return (
    // @ts-expect-error
    <PopOver
      display={display}
      popOver={
        <div
          className={classNames(styles.popOverInner, {
            [styles.hidden]: hidden,
          })}
          style={{ minHeight, minWidth, ...previewStyle }}
        >
          <PopOverContainer fullHeight width="auto" noPadding>
            {popOver}
          </PopOverContainer>
        </div>
      }
      modifiers={modifiers}
      trigger="hover"
      popOverPortal={document.body}
      arrow={<PopOverContainerArrow />}
      placement={popOverPlacement}
      popOverOuterClassName={classNames(styles.popOver, {
        [styles.hidden]: hidden,
      })}
      {...popOverProps}
    >
      {children}
    </PopOver>
  );
};

export default ContentPreviewPopOver;
